import { type Currency, USD_TO_EUR } from "./currency";

const EUR_CURRENCY_FORMATTER = new Intl.NumberFormat("nl-NL", {
	style: "currency",
	currency: "EUR",
	maximumFractionDigits: 0,
});

const USD_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 0,
});

const EUR_COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat("nl-NL", {
	maximumFractionDigits: 1,
	notation: "compact",
});

const USD_COMPACT_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 1,
	notation: "compact",
});

function withSymbolSpace(formatted: string): string {
	return formatted.replace(/^([€$])\s*/, "$1\u00A0");
}

export function formatCurrency(valueCents: number, currency: Currency): string {
	const value = valueCents / 100;
	if (currency === "EUR") {
		const converted = value * USD_TO_EUR;
		return withSymbolSpace(EUR_CURRENCY_FORMATTER.format(converted));
	}
	return USD_CURRENCY_FORMATTER.format(value);
}

export function formatCompactCurrency(
	valueCents: number,
	currency: Currency,
): string {
	if (valueCents <= 0) return currency === "EUR" ? "€\u00A00" : "$0";
	const value = valueCents / 100;
	if (currency === "EUR") {
		const converted = value * USD_TO_EUR;
		if (converted >= 1_000) {
			const num = EUR_COMPACT_NUMBER_FORMATTER.format(converted);
			return `€\u00A0${num.toLowerCase()}`;
		}
		return withSymbolSpace(EUR_CURRENCY_FORMATTER.format(converted));
	}
	const converted = value;
	if (converted >= 1_000) {
		const formatted = USD_COMPACT_CURRENCY_FORMATTER.format(converted);
		return formatted.replace(/K$/, "k");
	}
	return USD_CURRENCY_FORMATTER.format(value);
}

const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", {
	style: "percent",
	maximumFractionDigits: 1,
});

// Formats a churn-style fraction (0.12 → "12%"). Currency-independent.
export function formatPercent(fraction: number): string {
	return PERCENT_FORMATTER.format(fraction);
}

export type SyncStaleness = "fresh" | "stale" | "very-stale" | "unknown";

export function getSyncStaleness(date?: Date | string | null): SyncStaleness {
	if (!date) return "unknown";
	const days = (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24);
	if (days >= 90) return "very-stale";
	if (days >= 30) return "stale";
	return "fresh";
}

export function formatSyncedAgo(date?: Date | string | null): string | null {
	if (!date) return null;
	const ms = Date.now() - new Date(date).getTime();
	const hours = Math.floor(ms / (1000 * 60 * 60));
	if (hours < 1) return "just now";
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	const months = Math.floor(days / 30);
	return `${months}mo ago`;
}

/**
 * Capitalize each word in a name
 * e.g., "john doe" -> "John Doe"
 */
export function capitalizeName(name: string | null | undefined): string | null {
	if (!name) return null;
	return name
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}
